import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CaretDown, Check } from '@phosphor-icons/react';
import { createRecoveryMnemonic } from '@trove/crypto';
import { ActionMenu, Button, Dialog, TextField, toastManager } from '@trove/ui';
import { useWorkspace } from '../workspace';
import {
  DEFAULT_OPEN_VAULT_ICON,
  DEFAULT_VAULT_ICON,
  VAULT_ICON_OPTIONS,
  vaultIconEmoji,
} from '../vaultIcons';
import styles from './VaultSwitcher.module.css';

export function VaultSwitcher() {
  const { workspace, vaults, switchVault, createRepository } = useWorkspace();
  const navigate = useNavigate();
  const [createMode, setCreateMode] = useState<'locked' | 'open' | null>(null);
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState(DEFAULT_VAULT_ICON);
  const [creating, setCreating] = useState(false);
  if (!workspace || workspace.mode === 'shared') return null;

  const startPrivateVault = () => {
    setCreateMode('locked');
    setLabel('');
    setIcon(DEFAULT_VAULT_ICON);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateMode(null);
    setLabel('');
    setIcon(DEFAULT_VAULT_ICON);
  };

  const selectVault = async (repositoryId: string) => {
    await switchVault(repositoryId);
    // Switching while viewing Shared leaves the URL unchanged and makes the action appear to do
    // nothing. Show the selected vault's files as confirmation instead.
    await navigate({ to: '/', search: {} });
  };

  const createVault = async () => {
    const nextLabel = label.trim();
    if (!createMode || !nextLabel) return;
    setCreating(true);
    try {
      const mnemonic = createMode === 'locked' ? createRecoveryMnemonic() : '';
      await createRepository(mnemonic, false, undefined, {
        mode: createMode,
        label: nextLabel,
        icon,
      });
      if (createMode === 'locked') {
        toastManager.add({
          title: 'Save this recovery phrase',
          description: mnemonic,
        });
      }
      setCreateMode(null);
      setLabel('');
    } catch (cause) {
      toastManager.add({
        title: 'Could not create private vault',
        description: cause instanceof Error ? cause.message : 'Try again.',
      });
    } finally {
      setCreating(false);
    }
  };

  const items = [
    ...vaults.map((vault) => ({
      label: vault.label,
      trailing:
        vault.repositoryId === workspace.repositoryId ? (
          <Check size={16} weight="bold" aria-label="Current vault" />
        ) : undefined,
      onClick: () => {
        void selectVault(vault.repositoryId).catch((cause) => {
          toastManager.add({
            title: 'Could not switch vault',
            description: cause instanceof Error ? cause.message : 'Try again.',
          });
        });
      },
    })),
    { label: 'divider', onClick: () => {}, separator: true },
    {
      label: 'New vault…',
      onClick: startPrivateVault,
    },
  ];
  const workspaceEmoji = vaultIconEmoji(workspace.icon);

  return (
    <>
      <ActionMenu
        header="Switch vault"
        trigger={
          <button className={styles.trigger} type="button" aria-label="Switch vault">
            <span className={styles.icon} aria-hidden="true">
              {workspaceEmoji}
            </span>
            <span className={styles.label}>{workspace.label}</span>
            <CaretDown size={12} className={styles.caret} />
          </button>
        }
        items={items}
      />
      <Dialog
        open={createMode !== null}
        onOpenChange={(open) => {
          if (!open) closeCreateDialog();
        }}
        title="Create a vault"
        description={
          createMode === 'open'
            ? 'For work and collaboration. Content is readable by the server for search and merge.'
            : 'End-to-end encrypted and protected by your recovery phrase.'
        }
        actions={
          <>
            <Button disabled={creating} onClick={closeCreateDialog}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={creating || !label.trim()}
              onClick={() => void createVault()}
            >
              Create {createMode === 'open' ? 'team space' : 'private vault'}
            </Button>
          </>
        }
      >
        <div className={styles.modeOptions} role="radiogroup" aria-label="Vault type">
          <button
            className={styles.modeOption}
            type="button"
            role="radio"
            aria-checked={createMode === 'locked'}
            onClick={() => {
              setCreateMode('locked');
              setIcon(DEFAULT_VAULT_ICON);
            }}
          >
            <span>🔒 Private vault</span>
            <small>End-to-end encrypted. You protect it with a recovery phrase.</small>
          </button>
          <button
            className={styles.modeOption}
            type="button"
            role="radio"
            aria-checked={createMode === 'open'}
            onClick={() => {
              setCreateMode('open');
              setIcon(DEFAULT_OPEN_VAULT_ICON);
            }}
          >
            <span>👥 Team space</span>
            <small>Server-readable workspace for collaboration, search, and merge.</small>
          </button>
        </div>
        <TextField
          autoFocus
          label={createMode === 'open' ? 'Team space name' : 'Private vault name'}
          value={label}
          disabled={creating}
          onValueChange={setLabel}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void createVault();
          }}
        />
        <div className={styles.iconPicker}>
          <span className={styles.iconPickerLabel}>Icon</span>
          <div className={styles.iconOptions}>
            {VAULT_ICON_OPTIONS.map(({ id, label: iconLabel, emoji }) => (
              <button
                key={id}
                className={styles.iconOption}
                type="button"
                aria-label={`Use ${iconLabel} as the vault icon`}
                aria-pressed={icon === id}
                onClick={() => setIcon(id)}
              >
                <span aria-hidden="true">{emoji}</span>
              </button>
            ))}
          </div>
        </div>
      </Dialog>
    </>
  );
}
